import { DB_STATE, MODULES, RIGHTS_LEVEL, SEC_GROUP_PRIORITY } from './enums';

export type DbID = number;
export type DbTimestamp = number;
export type DbBool = 1 | 0;

export interface DbProject {
  id: DbID;
  customer_id: DbID;
  asset_id: DbID | null;
  name: string;
  start_date: DbTimestamp;
  is_active: DbBool;
  is_superuser: boolean;
  is_permission_manager: boolean;
  is_tender_manager: boolean;
  is_tender_admin: boolean;
  is_log_manager: boolean;
  is_share_manager: boolean;
  is_share_admin: boolean;
  updated_at: DbTimestamp;
  concurrency_stamp: string | null;
  project_data?: ProjectData;
  contact_data?: ContactData;
  customer_data: CustomerData;
  type?: number; // 0=CWEB_TEMPLATE, 1=CUSTOMER_OWN_TEMPLATE, 2=HAS_PERMITTED_PROJECT, 3=OTHER_PROJECT
  editable?: boolean;
}

export interface ProjectData {
  name: string;
  address: string;
  zipcode: string;
  town: string;
  start_date: number;
  handover_date: number;
  updated_at: number;
  concurrency_stamp: string;
  is_secret: boolean;
  is_anonymous: boolean;
  case_nr: string;
}

export interface ContactData {
  logo_url: string;
  company_name: string;
  company_name_2: string;
  address: string;
  zipcode: string;
  city: string;
  phone: string;
  fax: string;
  cvr: string;
  email: string;
  http: string;
}

export interface CustomerData {
  customer_id: string;
  name: string;
  cvr: string;
  address: string;
  zipcode: string;
  city: string;
  contact_person: string;
  contact_email: string;
  contact_phone: string;
  logo: string;
}

export interface Option {
  alias_title: string;
  is_tender_folder: boolean;
  is_tender_root_folder: boolean;
  tender_parent_id: DbID;
  tender_registration_id: DbID;
}

export interface DbFolder {
  id: DbID;
  project_id: DbID;
  /** parent folder id, `null` for root */
  parent_id: DbID | null;
  state: DB_STATE;
  name: string;
  description: string;
  permission: RIGHTS_LEVEL;
  sector_id: DbID;
  is_empty: boolean;
  is_favorite: boolean;
  is_locked: boolean;
  is_notify: boolean;
  created_by: DbID;
  created_at: DbTimestamp;
  updated_by: DbID;
  updated_at: DbTimestamp;
  concurrency_stamp: string;
  highlight: number;
  focus: number;
  option: Option;
  options?: Option;
}

export interface DbFile {
  file_id?: string;
  id: DbID;
  project_id: DbID;
  title: string;
  description: string;
  state: DB_STATE;
  sector_description: string;
  /** parent folder id, `null` for root */
  folder_id: DbID | null;
  serial_number: number | null;
  category_id: DbID | null;
  category_number: number | null;
  module_serial_number: number | null;
  sector_id: DbID | null;
  module_id: DbID;
  is_favorite: boolean;
  is_notify: boolean;
  /** NOT folder_id!! */
  parent_id: DbID | null;
  created_by: DbID;
  created_at: DbTimestamp;
  client_created_at: DbTimestamp | null;
  updated_by: DbID;
  updated_at: DbTimestamp;
  concurrency_stamp: string;
  versions?: DbFileVersion[];
  version?: DbFileVersion;
  files?: DbFile[];
  location?: any;
  tags: number[] | null;
  registration_id?: number;
}

export interface DbFileVersion {
  id: string;
  project_id: DbID;
  versioned_file_id: DbID;
  name: string;
  size: number | null;
  state: DB_STATE;
  /** revision column value */
  version: string | null;
  revision: number | null;
  /** aws download link */
  location: string;
  created_by: DbID;
  created_by_name: string | null;
  created_at: DbTimestamp;
  updated_by: DbID;
  updated_at: DbTimestamp;
  description: string | null;
  registration_instance_description: string | null;
  extension_svf_model: DBExtensionSVFModel | null;
  is_svf_convertible: boolean;
}

export interface DbCommit {
  id: DbID;
  project_id: DbID;
  description: string;
  is_important: boolean;
  state: DB_STATE;
  created_by: DbID;
  created_at: DbTimestamp;
}

export interface DbSecurityGroup {
  id: DbID;
  project_id: DbID;
  priority: SEC_GROUP_PRIORITY;
  name: string;
  description: string;
  color: number | null;
  is_locked: boolean;
  created_by: DbID;
  created_at: DbTimestamp;
  updated_by: DbID;
  updated_at: DbTimestamp;
}

export interface DbFolderPermission {
  id: DbID;
  folder_id: DbID;
  security_group_id: DbID;
  description: string | null;
  level: RIGHTS_LEVEL;
  created_by: DbID;
  created_at: DbTimestamp;
  updated_by: DbID;
  updated_at: DbTimestamp;
}

export enum ResourceState {
  NONE,
  DRAFT,
  PARTIAL,
  AVAILABLE,
  ACCEPTED,
  REJECTED,
  COMPLETED,
  PROCESSED,
  VALIDATED,
  REMOVED,
  DELETED,
  EXPIRED,
}

export enum UI_SHARE_KEY_STATUS {
  AWAITING = 0,
  INVITED = 1,
  OPENED = 2,
  CLAIMED = 3,
  REJECTED = 4,
  DELETED = 5,
}

export type DbShare = {
  id: any;
  share_key_id: string;
  share_key_email: string;
  share_key_state: UI_SHARE_KEY_STATUS | ResourceState;
  share_key_title?: string;
  share_key_user_id?: number;
  share_key_sector_id?: number;
  share_key_expires_at: number;
  share_key_is_claimed: boolean;
  share_key_opened_at: number;
  share_key_claimed_at: number;
  share_key_first_invite_at: number;
  share_id: string;
  share_active_from: number;
  share_expires_at: number;
  share_title?: string;
  share_description?: string;
  share_state: ResourceState;
  module: number;
  project_id: number;
  registration_title?: string;
  registration_description?: string;
  share_key_last_rejected_at?: number;
  project_data: ProjectShortInfoViewModel;
  offers: offerContract[];
  // Real fields, confirmed present in the current /share_keys schema but
  // previously untyped/missing here.
  claimer?: ShareKeyClaimerVM;
  share_key_messages?: string[];
  // GUESS: not yet shown in /share_keys schema, following the naming
  // convention already confirmed on GET /shares/{share_id}, /my_shares and
  // /my_tender_shares (Primary-/Secondary Share Keys feature).
  // Rename here if backend ships different field names for this specific endpoint.
  // CONFIRMED OBSOLETE for GET /projects/{id}/tenders/{id}/share_keys (the
  // admin listing this DbShare type is used for): backend's Tender Admin -
  // Secondary Share Keys Update clarified this listing returns primary
  // rows only, plus the new has_secondary_share_keys flag below - it never
  // mixed in secondary rows filterable by these two fields. Kept as
  // legacy optional fields (still real elsewhere, e.g. /my_shares), just
  // no longer relied on for this endpoint's admin "+" drawer.
  share_is_secondary_key_on?: boolean;
  share_key_is_primary?: boolean;
  share_key_parent_id?: string | null;
  parent_inviter?: ParentInviterVM | null;
  // Primary-/Secondary Share Keys (Tender Admin Update): present on the
  // primary share key response of GET /projects/{id}/tenders/{id}/share_keys.
  // When true, this vendor has secondary share keys, fetchable via
  // GET .../share_keys/{share_key}/secondary_share_keys (see
  // useAdminSecondaryShareKeys.ts).
  has_secondary_share_keys?: boolean;
};

/**
 * claimer: basic info of the user who claimed a share key (admin /share_keys list)
 */
export interface ShareKeyClaimerVM {
  id: number;
  email: string;
  full_name: string;
  company_name: string;
  phone_land_code: string;
  phone_nr: string;
  mobile_land_code: string;
  mobile_nr: string;
}

/**
 * parent_inviter: basic information of the parent (primary) share key -
 * present on secondary share keys. Matches the /shares/{share_id},
 * /my_shares, /my_tender_shares "parent_inviter" payload shape.
 */
export interface ParentInviterVM {
  contact_key: string;
  id: number;
  type: string;
  state: number;
  contact_key_state: number;
  created_at: number;
  title: string;
  name_1: string;
  name_2: string;
  user_title_1: string;
  user_title_2: string;
  email_1: string;
  email_2: string;
  phone_1: string;
  phone_2: string;
  company_title: string;
  company_subtitle: string;
  company_email: string;
  company_url: string;
  location_1: string;
  location_2: string;
  address_1: string;
  address_2: string;
  postal_code: string;
  region_1: string;
  region_2: string;
  city: string;
  country: string;
  timezone: string;
}

/**
 * List item returned by GET /v1/tender_shares/{share_id}/secondary_share_keys.
 * Distinct from DbShare - no share_id/offers/module, includes claimer inline.
 */
export interface SecondaryShareKeyVM {
  share_key_id: string;
  share_key_title: string;
  share_key_email: string;
  share_key_state: number;
  share_key_user_id: number;
  share_key_first_invite_at: number | null;
  share_key_claimed_at: number | null;
  share_key_opened_at: number | null;
  share_key_last_rejected_at: number | null;
  share_key_expires_at: number | null;
  share_key_is_claimed: boolean;
  share_key_parent_id: string;
  share_key_is_primary: boolean;
  claimer: ShareKeyClaimerVM | null;
}

export type offerContract = {
  is_offer_created: boolean;
  offer_id: number;
  state: number;
  contract_id: number;
  contract_reference?: string;
};

export interface ProjectShortInfoViewModel {
  name: string;
  address?: string;
  zipcode?: string;
  town: string;
  start_date: number;
  updated_at: number;
  concurrency_stamp?: string;
  is_secret?: boolean;
  is_anonymous?: boolean;
  case_nr?: string;
}

export type DbSharedStatusData = {
  share_key_id: string;
  share_id: string;
  email: string;
  expires_at: number;
  state: DB_STATE;
  registration: {
    title: string;
    description: string;
    contract_id: number;
    vendor_category: number;
    share_id: string;
    active_from: number;
    active_until: number;
    winner_offer_id: number;
    tender_root_folder_ids: number[];
  };
  tender_root_folders: DbTenderFolder[];
};

// export interface DbShareKey {
//   id: string;
//   share_id: ShareKeyVM['id'];
//   key: string;
//   email: string;
//   state: DB_STATE;
//   title: string;
//   concurrency_stamp: string;
//   created_by: DbID;
//   created_at: DbTimestamp;
//   updated_by: DbID;
//   updated_at: DbTimestamp;
// }

export interface DbShareTender {
  title: string;
  description: string;
  active_from: DbTimestamp;
  active_until: DbTimestamp;
  attachments: DbShareTenderAttachment[];
}

export interface DbShareTenderFolder {
  id: DbID;
  name: string;
  description: string | null;
  alias_title: string;
}

export interface DbShareTenderAttachment {
  name: string;
  description: string | null;
}

export interface DbFolderProperties {
  folder_id: DbID;
  name: string;
  item_count: number;
  total_size: number;
  created_by: DbID;
  created_at: DbTimestamp;
  updated_at: DbTimestamp;
}

export interface AuthCredentials {
  username: string;
  password: string;
}

export interface DbModuleFile {
  id: DbID;
  project_id: DbID;
  created_at: number;
  module: MODULES;
  sector: string;
  sector_id: DbID | null;
  category: string;
  category_id: DbID | null;
  created_by: string | null;
  recipient: string | null;
  subject: string;
  description: string;
  plan: string;
  plan_id: DbID;
  reg_type: string;
  mime: string;
  file_name: string;
  file_path: string;
  location: string;
  versions?: DbFileVersion[];
}
export interface DbFileActionLog {
  action_id: number;
  action_at: number;
  action_type: string;
  create_by: number;
  create_by_name: string;
  file_id: string;
  file_name: string;
}
export interface DbFolderActionLog {
  action_id: number;
  action_at: number;
  action_type: string;
  create_by: number;
  create_by_name: string;
  folder_id: string;
  folder_name: string;
}

export interface DbUser {
  id: number;
  user_name: string;
  initials: string;
  user_email: string;
  workgroup_id: number;
  archive_active: boolean;
}

export interface DbUserSettings {
  user_id: number;
  block_notification: boolean;
  vacation: number | null;
  frequency: number | null;
  time_1: string | null;
  time_2: string | null;
  is_send_weekend: boolean;
  is_tags_on: boolean;
}

export interface DbFolderPermissionActionLog {
  action_id: number;
  action_at: number;
  action_type: string;
  create_by: number;
  create_by_name: string;
  folder_id: number;
  folder_name: string;
  folder_path: string;
  security_group_name: string;
}

export interface DbFocusOptions {
  value: number;
  name: string;
  color_hex_code: string;
  color_hex_code_opacity?: string;
}

export interface DbTenderFolder {
  id: DbID;
  folder_id?: DbID;
  project_id: DbID;
  parent_id: DbID | null;
  state: DB_STATE;
  name: string;
  description: string;
  created_by: DbID;
  created_at: DbTimestamp;
  updated_by: DbID;
  updated_at: DbTimestamp;
  module: MODULES;
  permission: RIGHTS_LEVEL;
  options: {
    alias_title: string;
    is_tender_root_folder: boolean;
    tender_registration_id: DbID | null;
    root_folder_id: DbID | null;
    sub_folder_ids: DbID[];
  };
}

export type DbTenderBase = {
  title: string;
  description: string | null;
  active_from: DbTimestamp;
  active_until: DbTimestamp;
};

export type DbTender = DbTenderBase & {
  winner_offer_id: DBId | null;
  tender_root_folder_ids: DbID[] | null;
  contract_id: DbID | null;
  vendor_category: number | null;
  share_id: string;
  share_key: string;
  tender_root_folders: DbTenderFolder[];
  id: DbID;
  project_id: DbID;
  instance_id: DbID;
  serial_number: number | null;
  category_id: DbID | null;
  category_number: number | null;
  module_serial_number: number | null;
  sector_id: DbID | null;
  module: MODULES;
  parent_id: DbID | null;
  created_by: DbID;
  created_at: DbTimestamp;
  client_created_at: DbTimestamp | null;
  updated_by: DbID;
  updated_at: DbTimestamp;
  concurrency_stamp: string;
  name?: string;
  discipline_id: DbID | null;
  deadline_at: DbTimestamp | null;
  is_deviation: DbBool;
  deviation_description: string | null;
  gps_point: any | null;
  state: ResourceState;
  subject_area: any | null;
  severity: any | null;
  priority: any | null;
  folder_id: DbID | null;
  instance_created_by: DbID;
  instance_created_at: DbTimestamp;
  instance_client_created_at: DbTimestamp | null;
  is_favorite: DbBool;
  is_notify: DbBool;
  tag: any | null;
  reference: string | null;
  registration_auditing_user: any | null;
  registration_assigned_user: any | null;
  registration_observing_user: any | null;
  registration_notifying_user: any | null;
  registration_assisting_workgroup: any | null;
  registration_assigned_workgroup: any | null;
  isOnTenderPropertyPanel?: boolean;
  shareKey?: string;
  shareId?: string;
  global_serial?: string;
  public?: TenderRegistrationPublicVM | null;
};

export type RegistrationLabelValuesVM = {
  group_id: number;
  group_name: string;
  registration_id?: number;
  labels: {
    label_id: number;
    label_title: string;
    value: string;
  }[];
};

export type TenderFolderInRegViewModel = {
  folder_id: number;
  title?: string;
  alias_title: string;
  description?: string;
  updated_at: number;
};

export type TenderRootFolderCoreVM = {
  id: number;
  name: string;
  alias_title: string;
  description: string;
};

export type TenderRegistrationVM = {
  id: number;
  project_id: number;
  title: string;
  description?: string;
  reference?: string;
  contract_id: number;
  created_by: number;
  created_at: number;
  updated_by: number;
  updated_at: number;
  vendor_category?: number;
  state: number;
  share_id: string;
  share_title?: string;
  share_description?: string;
  // Confirmed via GET /projects/{project_id}/tenders/{tender_id} - the
  // Primary-/Secondary Share Keys activation flag lives on this tender/share
  // object, not on individual share_keys list items.
  share_is_secondary_key_on?: boolean;
  active_from?: number;
  active_until?: number;
  deadline_at?: number;
  publication_date?: number;
  winner_offer_id?: number;
  execution_period?: string;
  execution_period_data?: number;
  publication_at?: number;
  qa_on?: boolean;
  qa_start?: number;
  qa_stop?: number;
  location_1?: string;
  location_2?: string;
  contractor_title?: string;
  contractor_subtitle?: string;
  results_deadline?: number;
  results_details?: string;
  tender_root_folders: TenderFolderInRegViewModel[];
  label_values: RegistrationLabelValuesVM[];
  contacts_information: ContactInformationVM[];
  contractor_information?: ContactInformationVM;
  global_serial?: string;
  public?: TenderRegistrationPublicVM | null;
};

export type TenderRegistrationPublicVM = {
  state: number;
  created_at: number;
  created_by_name: string;
  removed_at?: number | null;
  removed_by_name?: string | null;
  removed_reason?: string | null;
  link: string;
};

export type TenderCoreRegVM = {
  title: string;
  description?: string;
  state?: number;
  share_title?: string;
  // See TenderRegistrationVM.share_is_secondary_key_on - kept in sync here
  // for TenderActionsProps.tenderData's union type safety.
  share_is_secondary_key_on?: boolean;
  active_from?: number;
  active_until?: number;
  execution_period?: string;
  execution_period_data?: number;
  deadline_at?: number;
  publication_date?: number;
  publication_at?: number;
  qa_on?: boolean;
  qa_start?: number;
  qa_stop?: number;
  location_1?: string;
  location_2?: string;
  contractor_title?: string;
  contractor_subtitle?: string;
  results_deadline?: number;
  results_details?: string;
  reference?: string;
  root_folders: TenderRootFolderCoreVM[];
  label_values: RegistrationLabelValuesVM[];
  contacts_information: ContactInformationVM[];
  contractor_information?: ContactInformationVM;
  global_serial?: string;
  public?: TenderRegistrationPublicVM | null;
};

export type ContactInformationVM = {
  contact_key: string;
  id: number;
  state: DB_STATE;
  contact_key_state: DB_STATE;
  created_at: string;
  type?: string;
  title?: string;
  name_1?: string;
  name_2?: string;
  user_title_1?: string;
  user_title_2?: string;
  email_1?: string;
  email_2?: string;
  phone_1?: string;
  phone_2?: string;
  company_title?: string;
  company_subtitle?: string;
  company_email?: string;
  company_url?: string;
  location_1?: string;
  location_2?: string;
  address_1?: string;
  address_2?: string;
  postal_code?: string;
  region_1?: string;
  region_2?: string;
  city?: string;
  country?: string;
  timezone?: string;
};

export interface DbTenderUser {
  id: number;
  email: string;
  full_name: string;
  firm_name: string;
  address: string;
  zipcode: string;
  town: string;
  phone_landcode: string | null;
  phone_nr: string;
  mobile_landcode: string;
  mobile_nr: string | null;
  user_initials: string;
  disabled: boolean;
  workgroup: {
    id: number;
    group_initials: string;
    group_name: string;
    group_job_description: string | null;
  };
  tender_user_type: number;
}

export interface DbAttachment {
  id: string;
  folder_id?: number;
  registration_id: DbID;
  registration_instance_id: DbID;
  project_id: DbID;
  name: string;
  title?: string;
  description: string | null;
  size: number;
  state: DB_STATE;
  location: string | null;
  file_labels: string | null;
  created_by: DbID;
  created_at: DbTimestamp;
  updated_by: DbID;
  updated_at: DbTimestamp;
  serial_number: number;
  version: string | null;
  version_at: DbTimestamp;
  files?: DbFile[];
}

export interface DbTenderSVFAttachment extends DbAttachment {
  tender_id: DbID;
  sourceType: 'tender-admin-root-svf';
  title?: string;
  file_name?: string;
}

type DbTenderInsideSVFFile = DbFile & {
  sourceType: 'tender-admin-inside-svf';
  tender_id: number;
  root_folder_id: number;
  folder_id: number;
  versioned_file_id: number;
  file_guid: string;
  file_name?: string;
};

export type DbShortFolderItem = {
  id: number;
  name: string;
  alias_title: string;
  description: string;
};

export type ArchiveRegistrationCore = {
  id: number;
  title: string;
  description: string | null;
  module: number | null;
  reference: string | null;
};

export type TenderSubFolderCoreVM = {
  id: number;
  name: string;
  description: string | null;
  parent_id: number | null;
  registrations: ArchiveRegistrationCore[];
}[];

type ShortTenderUser = Pick<DbTenderUser, 'id' | 'full_name' | 'email'>;

export interface DbQuestion {
  id: number;
  question: string;
  created_at: DbTimestamp;
  created_share_key_id?: string;
  created_by?: ShortTenderUser;
  answers?: DbAnswer[];
  change_instances?: number;
  state?: DB_STATE;
  report_data?: {
    is_ignore: boolean;
    is_public: boolean;
    is_report_sent: boolean;
    first_report_datetime: number | null;
    is_in_current_report: boolean;
  };
}

export interface DbQuestionChangeLogItem
  extends Pick<DbQuestion, 'question' | 'created_by' | 'created_at'> {
  instance_id: number;
}

export interface DbAnswer {
  id: number;
  question_id: number;
  answer: string;
  created_at: DbTimestamp;
  updated_at: DbTimestamp;
  created_by?: ShortTenderUser;
  updated_by?: ShortTenderUser;
  created_share_key_id?: string;
  state?: DB_STATE;
}

export interface DbOrderDraftVoucher {
  id: number;
  name: string;
  description?: string;
  unit_type: number;
  unit_count: number;
  created_at: number;
  created_by: number;
  sort: number | null;
}

export interface DbOrderVoucher extends DbOrderDraftVoucher {
  offer_voucher_requirement_option?: {
    is_ask_price?: boolean;
  };
  contract_id: number;
  sort: number | null;
}

export type DbVendorInformation = {
  company_name: string;
  company_origin_country: number;
  company_organization_number: string | null;
  company_main_email: string | null;
  company_main_phone: string | null;
  company_address_1: string | null;
  company_address_2: string | null;
  contact_title: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
};

export type DbOrderDetails = {
  id: number;
  registration_id: number;
  share_key: string;
  state: number;
  account_id: number;
  contract_id: number;
  sum_price: number;
  created_at: number;
  created_by: number;
  updated_at: number;
  updated_by: number;
  vouchers: DbOrderVoucherOrGroup[];
  vendor_information: DbVendorInformation;
};

export type DbOrderVoucherOrGroup = {
  id: number;
  name: string;
  description?: string;
  contract_id: number;
  sort: number | null;
  created_at: number;
  created_by: number;
  vouchers?: DbOrderVoucherOrGroup[];
  voucher_group_price_sum?: number | null;
  offer_voucher_option?: {
    price: number;
    state: number;
    updated_by: number;
    updated_at: number;
  } | null;
  offer_voucher_requirement_option?: {
    is_ask_price: boolean;
  };
} & (
  | {
      is_voucher_group: true;
      unit_type: null;
      unit_count: null;
      voucher_group_option: {
        voucher_group_contract_id: number;
        sub_voucher_ids: number[];
      };
    }
  | {
      is_voucher_group: false;
      unit_type: number;
      unit_count: number;
      voucher_group_option: null;
    }
);

export interface DbOrderVoucherGroup {
  id: number;
  name: string;
  description?: string;
  sort: number;
  is_ask_price: boolean;
  vouchers?: DbOrderVoucher[];
}

export interface TenderOfferVoucherVM {
  voucher_id: number | null;
  sum: number | null;
  is_approved: boolean | null;
  attachments: any[];
  voucher_type: number | null;
}

export interface TenderOfferVendorInformation {
  company_name: string | null;
  company_origin_country: number | null;
  company_organization_number: string | null;
  company_main_email: string | null;
  company_main_phone: string | null;
  company_address_1: string | null;
  company_address_2: string | null;
  contact_title: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  logo_file: string | null;
}

export interface TenderOfferVM {
  id: number | null;
  state: number | null;
  created_by: number | null;
  created_at: number | null;
  created_by_name: string | null;
  updated_by_name: string | null;
  updated_by: number | null;
  updated_at: number | null;
  vendor_information: TenderOfferVendorInformation;
  contract_id: number | null;
  share_key_id: string;
}

export interface UserInformWithAccountViewModel {
  id: number;
  email: string;
  is_customer_admin: boolean;
  account_ids: number[];
}

export interface DBExtensionSVFModel {
  file_id: string;
  state: number;
  autodesk_bucket_region: string;
  autodesk_object_id: string;
}

// dbShareListItem.ts

export type DbShareListResponse = DbShareListItem[];

/**
 * Root item of /my_shares
 */
export interface DbShareListItem {
  share_id: string;
  project_id: number;

  share_title: string | null; // can be null or empty string
  share_description: string; // can be empty string
  share_active_from: number | null; // unix seconds
  share_expires_at: number | null; // unix seconds
  share_state: number; // e.g. 3
  module: number; // e.g. 15

  contacts: DbShareContact[]; // array can be empty

  key: DbShareKey; // required in samples
  registration: DbShareRegistration | null; // object present in samples
  project: DbShareProject | null; // object present in samples

  // Primary-/Secondary Share Keys: whether secondary share key invitation
  // is enabled for the tender registration behind this share. Confirmed on
  // GET /v1/shares/{share_id}, /v1/my_shares, /v1/my_tender_shares.
  share_is_secondary_key_on?: boolean;
}

/**
 * contacts: currently unknown structure (samples show empty array).
 * Keep it flexible but typed for future extension.
 */
export interface DbShareContact {
  // Extend when backend provides structure
  [k: string]: unknown;
}

/**
 * key: info about the specific share key (critical for Share-Key header)
 */
export interface DbShareKey {
  share_key_id: string; // e.g. "857132498CADE74B7F401A29259ED703"
  share_key_title: string; // can be empty string
  share_key_email: string; // email address
  share_key_state: number; // e.g. 4
  share_key_user_id: number; // e.g. 12699
  share_key_first_invite_at: number | null; // unix seconds or null
  share_key_claimed_at: number | null; // unix seconds or null
  share_key_opened_at: number | null; // unix seconds or null
  share_key_last_rejected_at: number | null; // unix seconds or null
  share_key_expires_at: number | null; // unix seconds or null
  validity_status: DbShareKeyValidityStatus;

  // Primary-/Secondary Share Keys: confirmed on GET /v1/shares/{share_id},
  // /v1/my_shares, /v1/my_tender_shares.
  share_key_is_primary?: boolean;
  share_key_parent_id?: string | null;
  parent_inviter?: ParentInviterVM | null;
}

export interface DbShareKeyValidityStatus {
  is_key_active: boolean;
  is_key_expired: boolean;
  is_key_claimed: boolean;
  is_key_rejected: boolean;
}

/**
 * registration: tender registration summary
 */
export interface DbShareRegistration {
  registration_id: number;
  registration_title: string; // can be non-empty string
  registration_description: string; // can be empty string
  registration_reference: string | null;
  registration_state: number; // e.g. 3
  registration_instance_deadline_at: number;
}

/**
 * project: related project info
 */
export interface DbShareProject {
  name: string; // project name
  prefix: string | null;
  address: string | null;
  zipcode: string | null;
  town: string | null;

  start_date: number | null; // unix seconds
  handover_date: number | null; // unix seconds
  updated_at: number | null; // unix seconds

  concurrency_stamp: string | null;
  is_secret: boolean;
  is_anonymous: boolean;
  contact: unknown | null; // backend returns null in samples
}

export type ReviewDashboard = {
  total_reviews: number; // Total number of reviews
  total_ratings: number; // Sum of all ratings
  avg_star_rating: number; // Average rating
  star_max_value: number; // Maximum possible rating
  star_min_value: number; // Minimum possible rating
  star_allow_half: boolean; // Whether half-star ratings are allowed
  star_step: number; // Rating increment step
  last_review_time: number; // Timestamp of most recent review
  last_calculated_time: number; // Last time stats were calculated
  created_at: number;
  updated_at: number;
  star_1_count: number; // Count of 1-star ratings
  star_2_count: number; // Count of 2-star ratings
  star_3_count: number; // Count of 3-star ratings
  star_4_count: number; // Count of 4-star ratings
  star_5_count: number; // Count of 5-star ratings
};

// Project Application Types
export interface CurrentUserContext {
  cvr?: string;
  mail?: string;
  phone?: string;
  current_user?: {
    user_email?: string;
    user_name?: string;
    user_firm_name?: string;
    user_firm_address?: string;
    user_firm_zip?: string;
    user_firm_town?: string;
    user_phone?: string;
    user_mobile?: string;
  };
  customer?: {
    customer_id?: string;
    customer_company_name?: string;
    customer_cvr?: string;
    customer_address?: string;
    customer_zip?: string;
    customer_town?: string;
  };
}

export interface ProjectApplicationPayload {
  project: {
    name: string;
    address?: {
      road?: string;
      number?: string;
    };
    postal_code?: string;
  };
  owner?: {
    cvr?: string;
  };
  contact_information: {
    email: string;
    phone: string;
    contract_number?: string;
  };
  modules: {
    project: true;
    tender: {
      checked: boolean;
    };
    modeling_3d: {
      checked: boolean;
    };
  };
  remark?: string;
}

// =================================== OFFER NOTIFICATIONS

export type DbOfferNotificationTemplate = {
  id: number;
  title: string;
  description: string;
};

export type DbOfferNotificationRecipient = {
  id: number;
  email: string;
  state: number; // 1=pending, 2=sent, 3=delivered, 4=failed
  created_at: number;
  mailed_at: number;
  notification_template_title: string;
  notification__template_description: string; // Note: API has double underscore
  share_key: string;
  share_key_title: string; // Vendor name
  recipient_email_language_code: string;
  offer_id: number;
};
